import React, { useState, useRef, useCallback } from 'react';
import { motion, useMotionValue, useSpring, useTransform, Variants } from 'framer-motion';
import { Edit, Trash2, AlertTriangle, CheckCircle, Calendar, GraduationCap, FileText, Loader, RotateCcw, Mail, Phone, User } from 'lucide-react';

interface Card3DProps {
  record: any;
  onUpdate?: (id: string, data: any) => Promise<void>;
  onDelete?: (id: string, reason: string) => Promise<void>;
  onReportMistake?: (id: string, description: string) => Promise<void>;
  type?: 'institution' | 'student' | 'verifier' | 'view';
  className?: string;
}

const Card3D: React.FC<Card3DProps> = ({ 
  record, 
  onUpdate, 
  onDelete,
  onReportMistake, 
  type = 'view', 
  className = '' 
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isFlipped, setIsFlipped] = useState(false);
  const [mistakeReport, setMistakeReport] = useState('');
  const [showMistakeForm, setShowMistakeForm] = useState(false);

  const [editData, setEditData] = useState({
    full_name: record.full_name || '',
    current_year_sem: record.current_year_sem || '',
    passed_out: record.passed_out || '',
    gmail: record.gmail || '',
    phone_number: record.phone_number || '',
    cgpa: record.cgpa || '',
    backlogs: record.backlogs || '',
    remarks: record.remarks || ''
  });

  // Reduced 3D mouse tracking (less aggressive)
  const cardRef = useRef<HTMLDivElement>(null);
  const mouseX = useMotionValue(0);
  const mouseY = useMotionValue(0);

  const springConfig = { damping: 40, stiffness: 200 };
  const rotateX = useSpring(useTransform(mouseY, [-0.5, 0.5], [5, -5]), springConfig);
  const rotateY = useSpring(useTransform(mouseX, [-0.5, 0.5], [-5, 5]), springConfig);

  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    if (!cardRef.current || isEditing) return;

    const rect = cardRef.current.getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;

    mouseX.set((e.clientX - centerX) / (rect.width / 2));
    mouseY.set((e.clientY - centerY) / (rect.height / 2));
  }, [mouseX, mouseY, isEditing]);

  const handleMouseLeave = useCallback(() => {
    mouseX.set(0);
    mouseY.set(0);
  }, [mouseX, mouseY]);

  const handleUpdate = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    if (!onUpdate) return;

    setIsLoading(true);
    try {
      await onUpdate(record.roll_number, editData);
      setIsEditing(false);
    } catch (error) {
      console.error('Update failed:', error);
    } finally {
      setIsLoading(false);
    }
  }, [onUpdate, record.roll_number, editData]);

  const handleDelete = useCallback(async () => {
    if (!onDelete) return;
    
    const reason = prompt('⚠️ Why are you deleting this record? (This will notify the student):');
    if (reason !== null && reason.trim()) {
      setIsLoading(true);
      try {
        await onDelete(record.roll_number, reason);
      } catch (error) {
        console.error('Delete failed:', error);
      } finally {
        setIsLoading(false);
      }
    } else if (reason !== null) {
      alert('Please provide a reason for deletion.');
    }
  }, [onDelete, record.roll_number]);

  const handleReportMistake = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    if (!mistakeReport.trim() || !onReportMistake) return;

    setIsLoading(true);
    try {
      await onReportMistake(record.roll_number, mistakeReport);
      setMistakeReport('');
      setShowMistakeForm(false);
    } catch (error) {
      console.error('Report mistake failed:', error);
    } finally {
      setIsLoading(false);
    }
  }, [onReportMistake, record.roll_number, mistakeReport]);

  const getStatusColor = (status: string) => {
    const configs = {
      active: 'text-neon-green border-neon-green/50 bg-neon-green/10',
      deleted: 'text-red-400 border-red-400/50 bg-red-400/10',
      verified: 'text-neon-blue border-neon-blue/50 bg-neon-blue/10'
    };
    return configs[status as keyof typeof configs] || configs.active;
  };

  // Reduced animation variants
  const cardVariants: Variants = {
    hidden: { 
      opacity: 0, 
      scale: 0.95, 
      y: 20
    },
    visible: { 
      opacity: 1, 
      scale: 1, 
      y: 0,
      transition: { 
        type: "spring" as const, 
        duration: 0.4, 
        bounce: 0.1 
      }
    },
    hover: {
      y: -2,
      transition: { 
        type: "spring" as const, 
        duration: 0.2 
      }
    }
  };

  return (
    <div className="perspective-1000">
      <motion.div
        ref={cardRef}
        className={`relative w-full max-w-md mx-auto ${className}`}
        variants={cardVariants}
        initial="hidden"
        animate="visible"
        whileHover={!isEditing ? "hover" : undefined}
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
        style={{
          rotateX: isEditing ? 0 : rotateX,
          rotateY: isEditing ? 0 : rotateY,
          transformStyle: "preserve-3d"
        }}
      >
        <motion.div 
          className={`card-flip ${isFlipped ? 'flipped' : ''}`}
          animate={{ rotateY: isFlipped ? 180 : 0 }}
          transition={{ duration: 0.6, ease: "easeInOut" }}
        >
          {/* Front Face - Improved Layout */}
          <div className="card-flip-front glass-card p-6 rounded-xl border border-neon-blue/30">
            {/* Header - Only Roll Number and Status */}
            <div className="flex justify-between items-center mb-6">
              <div className="flex items-center space-x-3">
                <motion.div 
                  className="p-2 bg-gradient-to-br from-neon-blue/20 to-neon-purple/20 rounded-lg"
                  whileHover={{ scale: 1.05 }}
                >
                  <GraduationCap className="h-5 w-5 text-neon-blue" />
                </motion.div>
                <div>
                  <p className="text-neon-blue font-mono text-lg font-bold tracking-wider">
                    {(record.roll_number || 'ROLL NUMBER').toUpperCase()}
                  </p>
                </div>
              </div>
              
              <motion.div 
                className={`px-3 py-1 rounded-full text-xs font-bold border backdrop-blur-sm ${getStatusColor(record.status || 'active')}`}
                whileHover={{ scale: 1.05 }}
              >
                {record.status === 'deleted' ? 'DELETED' : (record.status || 'ACTIVE').toUpperCase()}
              </motion.div>
            </div>

            {/* Content */}
            {isEditing ? (
              <motion.form 
                onSubmit={handleUpdate} 
                className="space-y-4"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.3 }}
              >
                {/* Student Name */}
                <div>
                  <label className="block text-xs font-medium text-gray-300 mb-1">Student Name</label>
                  <input
                    type="text"
                    value={editData.full_name}
                    onChange={(e) => setEditData({...editData, full_name: e.target.value})}
                    className="w-full px-3 py-2 rounded-lg text-white text-sm bg-dark-card/80 border border-neon-blue/30 focus:border-neon-blue focus:outline-none"
                    placeholder="Full Name"
                    required
                  />
                </div>

                {/* Current Year & Sem / Passed Out */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-300 mb-1">Current Year & Sem</label>
                    <input
                      type="text"
                      value={editData.current_year_sem}
                      onChange={(e) => setEditData({...editData, current_year_sem: e.target.value})}
                      className="w-full px-3 py-2 rounded-lg text-white text-sm bg-dark-card/80 border border-neon-blue/30 focus:border-neon-blue focus:outline-none"
                      placeholder="e.g., IV/II"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-300 mb-1">Passed Out</label>
                    <input
                      type="text"
                      value={editData.passed_out}
                      onChange={(e) => setEditData({...editData, passed_out: e.target.value})}
                      className="w-full px-3 py-2 rounded-lg text-white text-sm bg-dark-card/80 border border-neon-blue/30 focus:border-neon-blue focus:outline-none"
                      placeholder="Year"
                    />
                  </div>
                </div>

                {/* Gmail */}
                <div>
                  <label className="block text-xs font-medium text-gray-300 mb-1">Gmail</label>
                  <input
                    type="email"
                    value={editData.gmail}
                    onChange={(e) => setEditData({...editData, gmail: e.target.value})}
                    className="w-full px-3 py-2 rounded-lg text-white text-sm bg-dark-card/80 border border-neon-blue/30 focus:border-neon-blue focus:outline-none"
                    placeholder="email@gmail.com"
                  />
                </div>

                {/* Phone Number */}
                <div>
                  <label className="block text-xs font-medium text-gray-300 mb-1">Phone Number</label>
                  <input
                    type="tel"
                    value={editData.phone_number}
                    onChange={(e) => setEditData({...editData, phone_number: e.target.value})}
                    className="w-full px-3 py-2 rounded-lg text-white text-sm bg-dark-card/80 border border-neon-blue/30 focus:border-neon-blue focus:outline-none"
                    placeholder="+91 XXXXXXXXXX"
                  />
                </div>

                {/* CGPA and Backlogs */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-300 mb-1">CGPA</label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      max="10"
                      value={editData.cgpa}
                      onChange={(e) => setEditData({...editData, cgpa: e.target.value})}
                      className="w-full px-3 py-2 rounded-lg text-white text-sm bg-dark-card/80 border border-neon-blue/30 focus:border-neon-blue focus:outline-none"
                      placeholder="0.00"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-300 mb-1">Backlogs</label>
                    <input
                      type="number"
                      min="0"
                      value={editData.backlogs}
                      onChange={(e) => setEditData({...editData, backlogs: e.target.value})}
                      className="w-full px-3 py-2 rounded-lg text-white text-sm bg-dark-card/80 border border-neon-blue/30 focus:border-neon-blue focus:outline-none"
                      placeholder="0"
                      required
                    />
                  </div>
                </div>

                {/* Remarks */}
                <div>
                  <label className="block text-xs font-medium text-gray-300 mb-1">Remarks</label>
                  <textarea
                    value={editData.remarks}
                    onChange={(e) => setEditData({...editData, remarks: e.target.value})}
                    className="w-full px-3 py-2 rounded-lg text-white text-sm h-16 resize-none bg-dark-card/80 border border-neon-blue/30 focus:border-neon-blue focus:outline-none"
                    placeholder="Additional remarks"
                  />
                </div>
                
                <div className="flex space-x-2 pt-2">
                  <motion.button
                    type="submit"
                    disabled={isLoading}
                    className="flex-1 bg-gradient-to-r from-neon-green to-green-500 px-4 py-2 rounded-lg text-white text-sm font-medium disabled:opacity-50"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    {isLoading ? <Loader className="animate-spin h-4 w-4 mx-auto" /> : 'Save'}
                  </motion.button>
                  <motion.button
                    type="button"
                    onClick={() => setIsEditing(false)}
                    className="flex-1 bg-gray-600 px-4 py-2 rounded-lg text-white text-sm font-medium"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    Cancel
                  </motion.button>
                </div>
              </motion.form>
            ) : (
              <>
                {/* Student Name - Prominently Displayed */}
                <div className="mb-6">
                  <h3 className="text-2xl font-bold text-white text-center">
                    {record.full_name || 'Student Name'}
                  </h3>
                </div>

                {/* Record Details - Organized Layout */}
                <div className="space-y-3">
                  {/* Academic Info */}
                  <div className="grid grid-cols-2 gap-3">
                    <div className="glass-card p-3 rounded-lg">
                      <div className="flex items-center space-x-2 mb-1">
                        <Calendar className="h-4 w-4 text-neon-purple" />
                        <p className="text-xs text-gray-400">Year & Semester</p>
                      </div>
                      <p className="text-white font-medium text-sm">
                        {record.current_year_sem || 'N/A'}
                      </p>
                    </div>
                    
                    <div className="glass-card p-3 rounded-lg">
                      <div className="flex items-center space-x-2 mb-1">
                        <GraduationCap className="h-4 w-4 text-neon-green" />
                        <p className="text-xs text-gray-400">CGPA</p>
                      </div>
                      <p className="text-white font-bold text-lg">
                        {record.cgpa || '0.00'}
                      </p>
                    </div>
                  </div>

                  {/* Contact Info */}
                  {(record.gmail || record.phone_number) && (
                    <div className="space-y-2">
                      {record.gmail && (
                        <div className="glass-card p-3 rounded-lg">
                          <div className="flex items-center space-x-2 mb-1">
                            <Mail className="h-4 w-4 text-blue-400" />
                            <p className="text-xs text-gray-400">Gmail</p>
                          </div>
                          <p className="text-white font-medium text-sm break-all">
                            {record.gmail}
                          </p>
                        </div>
                      )}
                      
                      {record.phone_number && (
                        <div className="glass-card p-3 rounded-lg">
                          <div className="flex items-center space-x-2 mb-1">
                            <Phone className="h-4 w-4 text-green-400" />
                            <p className="text-xs text-gray-400">Phone</p>
                          </div>
                          <p className="text-white font-medium text-sm">
                            {record.phone_number}
                          </p>
                        </div>
                      )}
                    </div>
                  )}
                  
                  {/* Backlogs */}
                  <div className="glass-card p-3 rounded-lg">
                    <div className="flex items-center space-x-2 mb-1">
                      <AlertTriangle className="h-4 w-4 text-yellow-400" />
                      <p className="text-xs text-gray-400">Number of Backlogs</p>
                    </div>
                    <p className="text-white font-medium">{record.backlogs || 0}</p>
                  </div>
                  
                  {/* Remarks */}
                  {record.remarks && (
                    <div className="glass-card p-3 rounded-lg">
                      <div className="flex items-center space-x-2 mb-1">
                        <FileText className="h-4 w-4 text-gray-400" />
                        <p className="text-xs text-gray-400">Remarks</p>
                      </div>
                      <p className="text-gray-300 text-sm leading-relaxed">{record.remarks}</p>
                    </div>
                  )}

                  {/* Passed Out Year */}
                  {record.passed_out && (
                    <div className="glass-card p-3 rounded-lg">
                      <div className="flex items-center space-x-2 mb-1">
                        <User className="h-4 w-4 text-indigo-400" />
                        <p className="text-xs text-gray-400">Passed Out</p>
                      </div>
                      <p className="text-white font-medium">{record.passed_out}</p>
                    </div>
                  )}
                </div>

                {/* Mistake Report Form */}
                {showMistakeForm && type === 'student' && (
                  <motion.form 
                    onSubmit={handleReportMistake} 
                    className="mt-4 p-4 glass-card rounded-lg border border-yellow-500/30"
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    exit={{ opacity: 0, height: 0 }}
                    transition={{ duration: 0.3 }}
                  >
                    <h4 className="text-sm font-medium text-yellow-400 mb-2">Report Mistake</h4>
                    <textarea
                      value={mistakeReport}
                      onChange={(e) => setMistakeReport(e.target.value)}
                      className="w-full px-3 py-2 rounded-lg text-white text-sm h-20 resize-none bg-dark-card/80 border border-yellow-400/30 focus:border-yellow-400 focus:outline-none"
                      placeholder="Describe the mistake you found..."
                      required
                    />
                    <div className="flex space-x-2 mt-2">
                      <motion.button
                        type="submit"
                        disabled={isLoading || !mistakeReport.trim()}
                        className="flex-1 bg-yellow-500 hover:bg-yellow-600 px-3 py-1 rounded text-white text-xs disabled:opacity-50"
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                      >
                        {isLoading ? <Loader className="animate-spin h-3 w-3 mx-auto" /> : 'Submit'}
                      </motion.button>
                      <motion.button
                        type="button"
                        onClick={() => setShowMistakeForm(false)}
                        className="flex-1 bg-gray-600 hover:bg-gray-500 px-3 py-1 rounded text-white text-xs"
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                      >
                        Cancel
                      </motion.button>
                    </div>
                  </motion.form>
                )}
              </>
            )}

            {/* Actions - No Excessive Hover Animation */}
            <div className="flex space-x-2 pt-4 border-t border-gray-600/50 mt-4">
              {type === 'institution' && (
                <>
                  <motion.button
                    onClick={() => setIsEditing(true)}
                    disabled={isLoading || record.status === 'deleted'}
                    className="flex-1 bg-gradient-to-r from-neon-blue to-blue-500 px-3 py-2 rounded-lg text-white text-sm font-medium disabled:opacity-50"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <Edit className="h-4 w-4 mr-1 inline" />
                    Edit
                  </motion.button>
                  
                  <motion.button
                    onClick={handleDelete}
                    disabled={isLoading || record.status === 'deleted'}
                    className="flex-1 bg-gradient-to-r from-red-500 to-pink-500 px-3 py-2 rounded-lg text-white text-sm font-medium disabled:opacity-50"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <Trash2 className="h-4 w-4 mr-1 inline" />
                    Delete
                  </motion.button>
                </>
              )}
              
              {type === 'student' && (
                <motion.button
                  onClick={() => setShowMistakeForm(!showMistakeForm)}
                  disabled={isLoading}
                  className="flex-1 bg-gradient-to-r from-yellow-500 to-orange-500 px-3 py-2 rounded-lg text-white text-sm font-medium"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <AlertTriangle className="h-4 w-4 mr-1 inline" />
                  Report Mistake
                </motion.button>
              )}
              
              {type === 'verifier' && (
                <motion.button
                  className="flex-1 bg-gradient-to-r from-neon-purple to-purple-500 px-3 py-2 rounded-lg text-white text-sm font-medium"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <CheckCircle className="h-4 w-4 mr-1 inline" />
                  Verified
                </motion.button>
              )}

              {/* Flip Button */}
              <motion.button
                onClick={() => setIsFlipped(!isFlipped)}
                className="p-2 bg-gradient-to-r from-gray-600 to-gray-700 rounded-lg text-white"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                title="Flip card to see details"
              >
                <RotateCcw className="h-4 w-4" />
              </motion.button>
            </div>
          </div>

          {/* Back Face - Additional Details */}
          <div className="card-flip-back glass-card p-6 rounded-xl border border-neon-blue/30">
            <div className="h-full flex flex-col">
              <h3 className="text-lg font-bold text-white mb-4 text-center bg-gradient-to-r from-neon-blue to-neon-purple bg-clip-text text-transparent">
                Audit Trail & Details
              </h3>
              
              <div className="flex-1 overflow-y-auto space-y-3">
                {record.audit_log && record.audit_log.slice(-5).map((log: any, index: number) => (
                  <motion.div 
                    key={log.id || index}
                    className="p-3 glass-card rounded-lg text-sm"
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.1 }}
                  >
                    <div className="flex justify-between items-start mb-1">
                      <span className={`font-medium ${
                        log.action === 'created' ? 'text-neon-green' :
                        log.action === 'updated' ? 'text-neon-blue' :
                        log.action === 'deleted' ? 'text-red-400' :
                        'text-gray-300'
                      }`}>
                        {log.action.toUpperCase()}
                      </span>
                      <span className="text-xs text-gray-400">
                        {new Date(log.timestamp).toLocaleDateString()}
                      </span>
                    </div>
                    <p className="text-xs text-gray-400">By: {log.by}</p>
                  </motion.div>
                ))}
                
                {(!record.audit_log || record.audit_log.length === 0) && (
                  <div className="text-center text-gray-400 py-8">
                    <FileText className="h-8 w-8 mx-auto mb-2 opacity-50" />
                    <p>No audit logs available</p>
                  </div>
                )}
              </div>
              
              <motion.button
                onClick={() => setIsFlipped(false)}
                className="mt-4 w-full bg-gradient-to-r from-gray-600 to-gray-700 px-4 py-2 rounded-lg text-white text-sm font-medium"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <RotateCcw className="h-4 w-4 mr-2 inline" />
                Back to Details
              </motion.button>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </div>
  );
};

export default Card3D;
